import { contrastColor } from "contrast-color";

export const TrackCircleChartUpdates = {
  mounted() {
    let previousItems = [];
    previousItems = updateCircleChart(this.el, JSON.parse(this.el.dataset.items), previousItems);

    this.el.addEventListener("update-circle-chart-data", (e) => {
      previousItems = updateCircleChart(this.el, e.detail.items, previousItems);
    });
  },
};

// Must match the r="44%" of the SVG circles, expressed in viewBox units
const CIRCLE_RADIUS = 44;

export function calculateItems(rawItems) {
  const nonNullItems = rawItems.filter((item) => item.value !== 0);
  const total = nonNullItems.reduce((sum, item) => sum + item.value, 0);
  const spaceBetweenItems = 2.5;
  const maxWithSpaces = 100 - spaceBetweenItems * (nonNullItems.length > 1 ? nonNullItems.length : 0);

  let offset = 0;
  const items = nonNullItems.map((item) => {
    const percentage = (100 * item.value) / total;
    const width = (maxWithSpaces * item.value) / total;

    const newItem = {
      ...item,
      percentage,
      width,
      offset,
    };

    offset = offset + width + spaceBetweenItems;

    return newItem;
  });

  return [items, total];
}

// Position (as percentages of the chart wrapper) of the middle of an item's arc.
// The stroke of an SVG <circle> starts at 3 o'clock and runs clockwise.
export function tooltipPosition(item) {
  const angle = (2 * Math.PI * (item.offset + item.width / 2)) / 100;

  return {
    left: 50 + CIRCLE_RADIUS * Math.cos(angle),
    top: 50 + CIRCLE_RADIUS * Math.sin(angle),
  };
}

function updateCircleChart(el, rawItems, previousItems) {
  const [items, total] = calculateItems(rawItems);
  computeCircleChart(el, items, previousItems);
  displayTotal(el, total);
  return items;
}

function computeCircleChart(el, items, previousItems) {
  const summaryContainer = el.querySelector(".js-circle__chart-summary");
  const summaryTemplate = summaryContainer.querySelector("template");
  summaryContainer.querySelectorAll(":scope > :not(template)").forEach((item) => item.remove());

  const svgContainer = el.querySelector("svg");
  const svgTemplate = svgContainer.querySelector("defs > circle");
  svgContainer.querySelectorAll(":scope > circle:not(.js-circle__chart-background-circle)").forEach((circle) => circle.remove());

  const segments = [];
  const summaryItems = [];

  items.forEach((item, index) => {
    const summaryClone = summaryTemplate.content.cloneNode(true);
    summaryClone.querySelector(".js-circle__chart-summary-color-indicator").style.backgroundColor = item.color;
    const labelElement = summaryClone.querySelector(".js-circle__chart-summary-label");
    labelElement.textContent = el.dataset.detailedSummary === "true" ? `(${item.percentage.toFixed(0)}%) ${item.label}` : item.label;
    labelElement.title = item.label;
    summaryContainer.appendChild(summaryClone);
    summaryItems.push(summaryContainer.lastElementChild);

    const svgClone = svgTemplate.cloneNode(true);

    svgClone.id = `circle-item-${el.dataset.id}-${index}`;
    svgClone.setAttribute("stroke", item.color);

    const previousItem = previousItems.find((i) => i.id === item.id) || { width: 0, offset: previousItems[index]?.offset || 0 };
    svgClone.setAttribute("stroke-dasharray", `${previousItem.width} ${100 - previousItem.width}`);
    svgClone.setAttribute("stroke-dashoffset", `-${previousItem.offset}`);

    setTimeout(() => {
      svgClone.setAttribute("stroke-dasharray", `${item.width} ${100 - item.width}`);
      svgClone.setAttribute("stroke-dashoffset", `-${item.offset}`);
    }, 10);

    svgClone.addEventListener("mouseenter", () => {
      showTooltip(el, item);
      highlightItem(segments, summaryItems, index);
    });
    svgClone.addEventListener("mouseleave", () => {
      hideTooltip(el);
      resetHighlight(segments, summaryItems);
    });

    svgContainer.appendChild(svgClone);
    segments.push(svgClone);
  });
}

function showTooltip(el, item) {
  const tooltip = el.querySelector(".js-circle__chart-tooltip");
  const value = [item.value.toLocaleString(window._locale), el.dataset.unit].filter(Boolean).join(" ");
  const { left, top } = tooltipPosition(item);

  tooltip.textContent = `${value} - ${item.percentage.toFixed(0)}%`;
  tooltip.style.backgroundColor = item.color;
  tooltip.style.color = contrastColor({ bgColor: item.color });
  tooltip.style.left = `${left}%`;
  tooltip.style.top = `${top}%`;
  tooltip.hidden = false;
}

function hideTooltip(el) {
  el.querySelector(".js-circle__chart-tooltip").hidden = true;
}

function highlightItem(segments, summaryItems, activeIndex) {
  segments.forEach((segment, index) => segment.classList.toggle("circle__chart-item--dimmed", index !== activeIndex));
  summaryItems.forEach((summaryItem, index) => {
    summaryItem.classList.toggle("circle__chart-summary-item--dimmed", index !== activeIndex);
    summaryItem
      .querySelector(".js-circle__chart-summary-label")
      .classList.toggle("circle__chart-summary-label--active", index === activeIndex);
  });
}

function resetHighlight(segments, summaryItems) {
  segments.forEach((segment) => segment.classList.remove("circle__chart-item--dimmed"));
  summaryItems.forEach((summaryItem) => {
    summaryItem.classList.remove("circle__chart-summary-item--dimmed");
    summaryItem.querySelector(".js-circle__chart-summary-label").classList.remove("circle__chart-summary-label--active");
  });
}

function displayTotal(el, total) {
  const totalText = [total.toLocaleString(window._locale), el.dataset.unit].filter(Boolean).join(" ");
  el.querySelector(".js-circle__chart-total-value").textContent = totalText;
}
